import { useMemo, useRef, useLayoutEffect } from 'preact/hooks';
import { marked } from 'marked';
import { pickLocale } from '../api.js';
import { applyScopeTemplate, escapeHtml } from '../util/template.js';

/**
 * Operator-authored markdown block. Parses `source[locale]` with `marked`
 * and pipes through an allow-listed sanitizer before mounting the result
 * into the DOM. The sanitizer drops every event handler, every
 * `<script>`, and every non-allow-listed tag — shoppers only see operator
 * copy that passed the allowlist below.
 *
 * Three Cs:
 *   - Configurable: every word on the surface comes from operator config
 *     (`source: Record<locale, string>`), not code.
 *   - Composable: the block knows nothing about the surrounding page; it
 *     only takes a locale-keyed source map.
 *   - Conversational: markdown is the most natural way for an operator
 *     (human or LLM) to draft shopper copy.
 *
 * @param props.source Locale → markdown source.
 * @param props.locale Active shopper locale; falls back to `en` then the
 *   first available entry (see {@link pickLocale}).
 */
export function MarkdownBlock(props: {
  source: Record<string, string>;
  locale: string;
  /**
   * Optional runtime scope for `{{ scope.foo }}` token substitution
   * BEFORE markdown parsing. Lets operators inject runtime values
   * (bedrock-generated tips, integration outputs) into their copy.
   * Tokens that miss render as empty strings; sanitizer runs after so
   * any injected HTML is still allowlisted.
   */
  scope?: Record<string, unknown>;
  /**
   * Opt-in: render substituted scope values AS markdown (parsed + sanitized)
   * rather than inert escaped text. Use only for operator-trusted RICH content
   * whose value is itself markdown — e.g. the LLM-generated weather tip, where
   * `**bold**` / `---` should format. The sanitizer remains the XSS gate. When
   * false (default) untrusted values stay inert (bd 6oc).
   */
  scopeMarkdown?: boolean;
}): preact.JSX.Element {
  const html = useMemo(() => {
    const raw = pickLocale(props.source, props.locale);
    if (props.scopeMarkdown && props.scope) {
      // Rich mode: substitute scope values RAW into the source, then parse
      // markdown + sanitize so the value's own markdown formats. The sanitizer
      // (allowlist) still strips scripts / handlers / unsafe hrefs, so XSS is
      // gated even for untrusted (LLM) content. Opt-in per block.
      return sanitize(parseMarkdownToHtml(applyScopeTemplate(raw, props.scope)));
    }
    // Default (safe) mode: parse + sanitize the OPERATOR template first (its
    // `{{ scope.x }}` tokens are inert text to the parser), THEN substitute
    // untrusted runtime values HTML-escaped into the final string — so they
    // never reach the markdown parser (no HTML injection / autolink / markdown
    // smuggling) and render as inert data (bd 6oc).
    const sanitized = sanitize(parseMarkdownToHtml(raw));
    return props.scope
      ? applyScopeTemplate(sanitized, props.scope, { escapeValue: escapeHtml })
      : sanitized;
  }, [props.source, props.locale, props.scope, props.scopeMarkdown]);
  const ref = useRef<HTMLDivElement | null>(null);
  // The sanitized string is mounted via an imperative HTML write so
  // tests + the live runtime see the same DOM shape. Every node here has
  // already passed the allowlist sweep — the `sanitize()` output is the
  // contract gate.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    (el as unknown as Record<string, string>).innerHTML = html;
  }, [html]);
  return <div ref={ref} class="prose prose-sm max-w-none text-on-surface-variant" />;
}

/**
 * Parse markdown to HTML. We disable async + ID generation so the output
 * stays minimal — operators rarely need header anchors and the email-
 * mangling pass adds opaque entities we'd have to allow-list.
 */
function parseMarkdownToHtml(src: string): string {
  if (!src) return '';
  // marked is synchronous when no async extensions are configured.
  // `parse` returns string here.
  const out = marked.parse(src, { async: false, gfm: true, breaks: false }) as string;
  return out;
}

// === Sanitizer ============================================================
// Allowlist (cited in JSDoc + epic spec): p, strong, em, code, pre,
// blockquote, ul, ol, li, h1-h6, a (href only; we force
// rel="noopener noreferrer" target="_blank"), br, hr.
//
// Strategy: a tiny regex pass over the marked output. The output is
// well-formed HTML (marked never produces partial tags), so a regex
// sweep — tag-by-tag — is enough. We do not run a full HTML parser to
// keep the runtime bundle ~1 KB instead of pulling a sanitizer lib.
//
// Rules:
//   1. Strip any tag (open, close, self-close) whose name isn't in
//      ALLOWED_TAGS. Strip its content too (so <script>alert(1)</script>
//      vanishes).
//   2. On a surviving tag, strip every attribute except `href` on <a>.
//   3. On every <a>, force rel="noopener noreferrer" target="_blank".

const ALLOWED_TAGS = new Set<string>([
  'p',
  'strong',
  'em',
  'code',
  'pre',
  'blockquote',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'a',
  'br',
  'hr',
]);

/**
 * Tags whose entire contents must be removed when the tag itself is not
 * allowed (e.g. `<script>...</script>`, `<style>...</style>`,
 * `<iframe>...</iframe>`). Without this step the regex pass would only
 * remove the tags and leave the inner text behind.
 */
const STRIP_WITH_CONTENT = ['script', 'style', 'iframe', 'object', 'embed', 'noscript'];

/**
 * Allow-listed HTML sanitizer. Cited allowlist:
 *   `p`, `strong`, `em`, `code`, `pre`, `blockquote`, `ul`, `ol`, `li`,
 *   `h1`-`h6`, `a` (href only, forced rel + target), `br`, `hr`.
 * Everything else is removed. Event handlers, inline styles, and
 * unknown attributes are stripped.
 */
export function sanitize(html: string): string {
  let out = html;

  // 1. Strip dangerous tag+content pairs.
  for (const t of STRIP_WITH_CONTENT) {
    const re = new RegExp(`<${t}\\b[^>]*>[\\s\\S]*?</${t}\\s*>`, 'gi');
    out = out.replace(re, '');
    // Also strip orphan opening or closing variants.
    out = out.replace(new RegExp(`</?${t}\\b[^>]*>`, 'gi'), '');
  }

  // 2. Walk every tag. Drop tags whose name isn't allow-listed; on the
  // allowed ones, normalise attributes.
  out = out.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>/g, (match, rawName: string, rest: string) => {
    const name = rawName.toLowerCase();
    if (!ALLOWED_TAGS.has(name)) return '';
    const isClosing = match.startsWith('</');
    if (isClosing) return `</${name}>`;
    const isSelfClose = /\/\s*$/.test(rest);

    if (name === 'a') {
      const hrefMatch = rest.match(/\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const rawHref = hrefMatch ? (hrefMatch[2] ?? hrefMatch[3] ?? hrefMatch[4] ?? '') : '';
      const safeHref = isSafeHref(rawHref) ? rawHref : '#';
      return `<a href="${escapeAttr(safeHref)}" rel="noopener noreferrer" target="_blank">`;
    }

    // Void elements with no attributes carried through.
    if (name === 'br' || name === 'hr') {
      return `<${name}/>`;
    }
    // Default: keep the tag, drop every attribute.
    return isSelfClose ? `<${name}/>` : `<${name}>`;
  });

  return out;
}

/** Cheap href safety filter: block javascript:, data:, vbscript: URLs. */
function isSafeHref(href: string): boolean {
  const trimmed = href.trim().toLowerCase();
  if (!trimmed) return false;
  if (trimmed.startsWith('javascript:')) return false;
  if (trimmed.startsWith('vbscript:')) return false;
  if (trimmed.startsWith('data:')) return false;
  return true;
}

/** Minimal attribute-value escaper for href injection. */
function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
